import { gameIconSvg } from '../components/GameIcon';

/**
 * The arena's food and pickups, drawn in the game's own style instead of emoji (which the OS draws,
 * so the arena looked different on every phone). One food per cell, picked from the cell number.
 */
export const FOODS = ['apple', 'cherries', 'strawberry', 'grapes', 'orange', 'banana', 'watermelon', 'pear', 'peach', 'carrot', 'donut', 'cookie', 'cupcake', 'candy', 'lollipop', 'pizza'] as const;

/** Each drawing's main color: the glow behind it and the sparks when it's eaten. */
const TINT: Record<string, string> = {
  apple: '#ff4a5e', cherries: '#e8203f', strawberry: '#ff4a5e', grapes: '#9a5cff', orange: '#ff9330', banana: '#ffd84a',
  watermelon: '#ff5a6e', pear: '#a8e04a', peach: '#ff9a7a', carrot: '#ff8a3a', donut: '#ff6aaa', cookie: '#e0a35c',
  cupcake: '#ff7ab8', candy: '#3ad6f0', lollipop: '#ff6aaa', pizza: '#ffcc3a',
  crown: '#ffc83d', trophy: '#ffc83d', robot: '#8fa0d8', ghost: '#c9c6ff', magnet: '#ff5f7a', speed: '#9be04a', shield: '#5a9cff',
  snowflake: '#8fe9ff', spiral: '#b07aff', bolt: '#ffe24d', flame: '#ff8a2a',
};
export const artTint = (name: string): string | undefined => TINT[name];

const images = new Map<string, HTMLImageElement>();
const decoded = new Set<string>();

/** Whether `name` is drawn art (not an emoji or text glyph). */
export const isArt = (name: string): boolean => name in TINT;

/**
 * The art as a decoded image, or null while it's still decoding (the first request starts it). Drawn
 * once into each sprite, so the SVG is rasterized at the sprite's size, sharp at any scale.
 */
export function artImage(name: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  let img = images.get(name);
  if (!img) {
    const svg = gameIconSvg(name, 128);
    if (!svg) return null;
    img = new Image();
    img.onload = () => decoded.add(name);
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    images.set(name, img);
  }
  return decoded.has(name) ? img : null;
}

/** Starts decoding all the art, so it's ready before the first food appears. */
export function preloadArt(): void {
  for (const name of Object.keys(TINT)) artImage(name);
}

/** The art once decoded (null where images can't load), for one-off drawings such as theme previews. */
export async function loadArt(name: string): Promise<HTMLImageElement | null> {
  artImage(name);
  const img = images.get(name);
  if (!img) return null;
  try {
    await img.decode();
    decoded.add(name);
    return img;
  } catch {
    return null;
  }
}
